import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { TacticPopularityScorePopup } from "@/components/features/Tactics/TacticPopularityScorePopup";
import DeleteTacticsActionModal from "@/components/molecules/ActionModals/DeleteTacticsActionModal/DeleteTacticsActionModal";
import UploadOrEditTacticsActionModal from "@/components/molecules/ActionModals/UploadOrEditTacticsActionModal/UploadOrEditTacticsActionModal";
import Pagination from "@/components/molecules/Pagination/Pagination";
import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { storeSignupContext } from "@/events/signupTracking";
import {
  trackCopyTacticsCode,
  trackExplore as trackTacticsExplore,
  trackFilter as trackTacticsFilter,
  trackSort as trackTacticsSort,
  trackViewContent as trackTacticsViewContent,
} from "@/events/tacticsMixpanel";
import TacticChosenSidebar from "@/features/Tactics/Sidebars/TacticChosenSidebar";
import TacticFiltersSidebar, {
  FORMATION_GROUPS_3_AT_THE_BACK,
  FORMATION_GROUPS_4_AT_THE_BACK,
  FORMATION_GROUPS_5_AT_THE_BACK,
} from "@/features/Tactics/Sidebars/TacticFiltersSidebar";
import TacticActiveFiltersRow from "@/features/Tactics/TacticActiveFiltersRow";
import TacticCard from "@/features/Tactics/TacticCard";
import TacticHeroSection from "@/features/Tactics/TacticHeroSection";
import TacticToolbar from "@/features/Tactics/TacticToolbar";
import TacticsSidePanel, {
  TacticsSidePanelOption,
} from "@/features/Tactics/TacticsSidePanel";
import {
  searchParamsToTacticsState,
  tacticsStateToSearchParams,
} from "@/features/Tactics/tacticsUrlState";
import { useScrollLock } from "@/hooks/useScrollLock";
import { useSticky } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { formationsAsListSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  Tactic,
  TacticSortOption,
  editTactic,
  fetchAuthors,
  fetchTactics,
  setFiltersAndSort,
  setSort,
  tacticsSelector,
  toggleFavoriteFilter,
} from "@/redux/tactics/tacticsSlice";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  clearSquad,
  setChosenTactic as setChosenSquadBuilderTactic,
} from "../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";

interface TacticEditModalProps {
  tacticName: string;
  tacticsDescription: string;
  tacticsCode: string;
  tacticId: string;
}

interface TacticPopularityScoreBannerProps {
  onClose: () => void;
  onReadMore: () => void;
}
const TacticPopularityScoreBanner = ({
  onClose,
  onReadMore,
}: TacticPopularityScoreBannerProps) => (
  <article className="py-2 w-full touch-device:rounded-lg flex items-center justify-between bg-panel-middleground px-2">
    <p className="text-sm">
      <Trans
        ns="tactics"
        i18nKey="page.popularityBanner"
        components={{
          more: (
            <span
              className="text-primary-action underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onReadMore();
              }}
            />
          ),
        }}
      />
    </p>
    <button className="p-2" onClick={onClose}>
      <CloseIcon />
    </button>
  </article>
);

export const TacticsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(["tactics", "common"]);
  const [scrollIntoBottom, refBottomBar] = useSticky({
    triggerOn: "inView",
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const {
    tactics,
    responses,
    sort,
    totalPages,
    selectedFilters,
    isFavoriteFilterOn,
  } = useSelector(tacticsSelector);
  const { user } = useSelector(getUserSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const formationsAsList = useSelector(formationsAsListSelector);
  const formationGroupLabels = useMemo(
    () => ({
      [FORMATION_GROUPS_3_AT_THE_BACK]: t("filters.threeAtTheBack"),
      [FORMATION_GROUPS_4_AT_THE_BACK]: t("filters.fourAtTheBack"),
      [FORMATION_GROUPS_5_AT_THE_BACK]: t("filters.fiveAtTheBack"),
    }),
    [t],
  );
  const [panelState, setPanelState] = useState<TacticsSidePanelOption>();
  const [activeTactic, setActiveTactic] = useState<Tactic>();
  const [showUploadTacticsModal, setShowUploadTacticsModal] = useState(false);
  const [showEditTacticsModal, setShowEditTacticsModal] =
    useState<TacticEditModalProps>();
  const [showDeleteTacticsModal, setShowDeleteTacticsModal] = useState<{
    tacticId: string;
  }>();
  const [page, setPage] = useState(1);
  const [urlStateApplied, setUrlStateApplied] = useState(false);
  const isLargeScreen = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  // Phones: both panels come up as sheets over the list rather than as a panel
  // that takes the page. From tablet up there is room beside the list, so the
  // side panel stays.
  const tacticSheetOpen =
    isMobile && panelState === "chosenTactic" && !!activeTactic;
  const filtersSheetOpen = isMobile && panelState === "filters";
  useScrollLock(tacticSheetOpen || filtersSheetOpen);
  const tacticLimit = useMemo(() => (isLargeScreen ? 10 : 8), [isLargeScreen]);
  const [showPopularityScoreBanner, setShowPopularityScoreBanner] = useState(
    localStorage.getItem("hasClosedPopularityScoreBanner") !== "true",
  );
  const [showPopularityScorePopup, setShowPopularityScorePopup] =
    useState(false);

  const handleClosePopularityScoreBanner = () => {
    localStorage.setItem("hasClosedPopularityScoreBanner", "true");
    setShowPopularityScoreBanner(false);
  };

  useEffect(() => {
    dispatch(fetchAuthors());
  }, [dispatch]);

  // Read filters and sort out of the URL once, so a shared link (an author
  // linking to their own tactics) lands on the same list.
  useEffect(() => {
    if (urlStateApplied) return;
    dispatch(
      setFiltersAndSort(
        searchParamsToTacticsState(searchParams, {
          formation: (value) => value,
          formationGroup: (value) => formationGroupLabels[value] ?? value,
        }),
      ),
    );
    setUrlStateApplied(true);
  }, [urlStateApplied, searchParams, dispatch, formationGroupLabels]);

  useEffect(() => {
    if (!urlStateApplied) return;
    const next = tacticsStateToSearchParams(searchParams, {
      selectedFilters,
      sort,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [urlStateApplied, selectedFilters, sort, searchParams, setSearchParams]);

  useEffect(() => {
    if (!urlStateApplied) return;
    const fetchData = async () => {
      setPage(1);
      await dispatch(fetchTactics({ page: 1, formationsAsList, tacticLimit }));
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    };
    fetchData();
  }, [dispatch, sort, selectedFilters, isFavoriteFilterOn, urlStateApplied]);

  const handleSortByChanged = (option: TacticSortOption) => {
    dispatch(setSort(option));
    trackTacticsSort({ value: option.value });
  };

  const handleChangePage = (page: number) => {
    setPage(page);
    dispatch(fetchTactics({ page, formationsAsList, tacticLimit }));
    if (window.scrollY > 262 && !isMobile) {
      window.scrollTo(0, 262);
    } else if (isMobile && window.scrollY > 192) {
      window.scrollTo(0, 192);
    }
  };

  const handleBuildSquadWithTactic = (tactic: Tactic) => {
    dispatch(
      setChosenSquadBuilderTactic({
        tactic: tactic,
        fromTacticsPage: true,
        initialLoad: false,
      }),
    );
    dispatch(clearSquad());
    navigate("/squad-builder");
    window.scrollTo(0, 0);
    dispatch(
      addAlert({
        title: t("common:success"),
        description: t("page.alerts.applied", { name: tactic.name }),
        type: "success",
      }),
    );
    trackTacticsExplore({
      source: "build squad with tactics",
      destination_section: "Squad Builder",
      destination_subsection: "Normal",
      new_content: tactic.isNew,
    });
  };

  const handleCopy = (eaCode: string) => {
    trackCopyTacticsCode();
    navigator.clipboard.writeText(eaCode).then(
      () => {
        dispatch(
          addAlert({
            title: t("common:success"),
            description: t("common:copiedToClipboard"),
            type: "success",
          }),
        );
      },
      (err) => {
        console.error("Failed to copy: ", err);
      },
    );
  };

  const handleClickFavorite = async (tactic: Tactic) => {
    if (user?.uuid) {
      if (activeTactic) {
        setActiveTactic({
          ...activeTactic,
          isFavorite: !activeTactic.isFavorite,
        });
      }
      await dispatch(
        editTactic({ id: tactic.id, isFavorite: !tactic.isFavorite }),
      );
      dispatch(
        addAlert({
          title: t("page.alerts.favoriteTitle"),
          description: !tactic.isFavorite
            ? t("page.alerts.addedToFavorites", { name: tactic.name })
            : t("page.alerts.removedFromFavorites", { name: tactic.name }),
          type: "success",
        }),
      );
    } else {
      // Overwrite messageSlice's generic hook-name write so the backend-fired
      // user_signed_up carries context=favourite + section/subsection.
      storeSignupContext("favourite", {
        section: "Tactics",
        subsection: "Overview",
      });
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "favorite-tactic",
        }),
      );
    }
  };

  const handleCloseSidebar = () => {
    // Fire filter on filters-panel close with the list of active filter keys.
    if (panelState === "filters") {
      const filterKeys: string[] = [];
      if (selectedFilters.positions.length > 0) filterKeys.push("positions");
      if (selectedFilters.formation.length > 0) filterKeys.push("formation");
      if (selectedFilters.formationGroup.length > 0)
        filterKeys.push("formationGroup");
      if (selectedFilters.author.length > 0) filterKeys.push("author");
      if (isFavoriteFilterOn) filterKeys.push("favorites");
      trackTacticsFilter({ filters: filterKeys });
    }
    setPanelState(undefined);
    setActiveTactic(undefined);
  };

  return (
    <main className="min-h-screen mx-auto bg-panel-base">
      <>
        <TacticHeroSection />
        <TacticToolbar
          panelState={panelState}
          isFavoriteFilterOn={isFavoriteFilterOn}
          loading={responses.getTactics.status === "pending"}
          canUploadTactic={user?.canUploadTactic}
          handleFiltersClicked={() =>
            setPanelState(panelState === "filters" ? undefined : "filters")
          }
          handleFavoriteClicked={() => {
            if (user?.uuid) {
              dispatch(toggleFavoriteFilter(!isFavoriteFilterOn));
            } else {
              dispatch(
                showModal({
                  modalType: "login-required",
                  freeFeatureLoggedInRequiredHook: "favorite-tactic",
                }),
              );
            }
          }}
          currentSortBy={sort}
          handleSortByChanged={handleSortByChanged}
          handleUploadTacticsClicked={() => {
            setShowUploadTacticsModal(true);
          }}
        />
        {(showUploadTacticsModal || showEditTacticsModal) && (
          <UploadOrEditTacticsActionModal
            onDismiss={() => {
              setShowUploadTacticsModal(false);
              setShowEditTacticsModal(undefined);
            }}
            type={showUploadTacticsModal ? "upload" : "edit"}
            tacticName={showEditTacticsModal?.tacticName || ""}
            tacticsCode={showEditTacticsModal?.tacticsCode || ""}
            tacticsDescription={showEditTacticsModal?.tacticsDescription || ""}
            tacticId={showEditTacticsModal?.tacticId || ""}
            responses={responses}
          />
        )}
        {showDeleteTacticsModal && (
          <DeleteTacticsActionModal
            onDismiss={() => setShowDeleteTacticsModal(undefined)}
            tacticId={showDeleteTacticsModal.tacticId}
            responses={responses}
          />
        )}
        {showPopularityScorePopup && (
          <TacticPopularityScorePopup
            onClose={() => setShowPopularityScorePopup(false)}
          />
        )}
        <div className="flex flex-row w-full max-w-content mx-auto">
          <div className="flex flex-col text-white gap-y-2 w-full mt-2 desktop:px-2 large:px-16">
            <div className="flex flex-row gap-x-2">
              <div
                className={cn(
                  "flex flex-col gap-y-2 w-full",
                  panelState && !isMobile && "hidden mobile:flex",
                )}
              >
                <TacticActiveFiltersRow />
                {showPopularityScoreBanner && (
                  <TacticPopularityScoreBanner
                    onClose={handleClosePopularityScoreBanner}
                    onReadMore={() => setShowPopularityScorePopup(true)}
                  />
                )}
                {tactics.length > 0
                  ? tactics.map((tactic, index) => (
                      <Fragment key={tactic.id}>
                        <TacticCard
                          tactic={tactic}
                          key={tactic.id}
                          active={activeTactic?.id === tactic.id}
                          handleCopy={handleCopy}
                          handleClickFavorite={() =>
                            handleClickFavorite(tactic)
                          }
                          handleBuildSquadWithTactic={() =>
                            handleBuildSquadWithTactic(tactic)
                          }
                          handleClickTacticCard={(tactic) => {
                            // Fire only when opening a tactic (not when toggling
                            // the same card closed).
                            if (activeTactic?.id !== tactic.id) {
                              trackTacticsViewContent({
                                new_content: tactic.isNew,
                              });
                            }
                            setActiveTactic(
                              activeTactic?.id === tactic.id
                                ? undefined
                                : tactic,
                            );
                            setPanelState(
                              panelState === "chosenTactic" &&
                                activeTactic?.id === tactic.id
                                ? undefined
                                : "chosenTactic",
                            );
                          }}
                          handleEditTactic={() =>
                            setShowEditTacticsModal({
                              tacticName: tactics[index].name,
                              tacticsDescription: tactics[index].description,
                              tacticsCode: tactics[index].eaCode,
                              tacticId: tactics[index].id,
                            })
                          }
                          handleDeleteTactic={() =>
                            setShowDeleteTacticsModal({
                              tacticId: tactics[index].id,
                            })
                          }
                        />
                        {index === tactics.length - 1 &&
                          tactics.length === tacticLimit && (
                            <div ref={refBottomBar} />
                          )}
                      </Fragment>
                    ))
                  : (responses.getTactics.status === "rejected" ||
                      responses.getTactics.status === "fulfilled") && (
                      <div className="mx-auto text-sm">
                        {t("page.noneFound")}
                      </div>
                    )}
              </div>
              {panelState && !isMobile && (
                <TacticsSidePanel
                  sidebarOption={panelState}
                  handleCloseSidebar={handleCloseSidebar}
                >
                  {panelState === "chosenTactic" && activeTactic ? (
                    <TacticChosenSidebar
                      tactic={activeTactic}
                      handleCopy={handleCopy}
                      isFavorite={activeTactic.isFavorite}
                      handleClickFavorite={() =>
                        handleClickFavorite(activeTactic)
                      }
                    />
                  ) : panelState === "filters" ? (
                    <TacticFiltersSidebar />
                  ) : null}
                </TacticsSidePanel>
              )}
              <BottomSheet
                isOpen={tacticSheetOpen}
                onClose={handleCloseSidebar}
                className="h-dvh max-w-full rounded-none bg-panel-background"
                header={{
                  title: t("sheet.tacticTemplate"),
                  closeLabel: t("sheet.closeTactic"),
                }}
              >
                {activeTactic && (
                  <TacticChosenSidebar
                    tactic={activeTactic}
                    handleCopy={handleCopy}
                    isFavorite={activeTactic.isFavorite}
                    handleClickFavorite={() =>
                      handleClickFavorite(activeTactic)
                    }
                  />
                )}
              </BottomSheet>
              <BottomSheet
                isOpen={filtersSheetOpen}
                onClose={handleCloseSidebar}
                className="h-dvh max-w-full rounded-none bg-panel-background"
                header={{
                  title: t("common:filters"),
                  closeLabel: t("sheet.closeFilters"),
                }}
              >
                <TacticFiltersSidebar />
              </BottomSheet>
            </div>
            {tactics.length > 0 && (
              <div className="flex justify-end w-full pt-2 pb-6">
                <Pagination
                  currentPage={page}
                  totalAmountOfPages={totalPages}
                  changePage={handleChangePage}
                />
                {tactics.length < tacticLimit && <div ref={refBottomBar} />}
              </div>
            )}
          </div>
        </div>
      </>
    </main>
  );
};
